import * as React from 'react';
import { flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

import { Checkbox, Pagination, Skeleton, Text } from '@/components/ui';

import DataGridFooter from './DataGridFooter';
import DataGridHeaderCell from './DataGridHeaderCell';
import DataGridToolbar from './DataGridToolbar';
import useAutoPageSize from './useAutoPageSize';
import useDataGridUrlState from './useDataGridUrlState';
import { buildFilterFn, resolveFilterMeta } from './filterStrategies';

// DataGrid — closed grid composition for tabular data with sort, per-column
// filter, pagination, selection and an actions toolbar. Wraps TanStack
// Table for the data pipeline (filter → sort → page) and composes ui/
// primitives for every visual surface. Lives in `business/` because the
// composition is non-trivial; consumers stay agnostic of TanStack.
//
// All data is client-side: caller passes the full `rows`, DataGrid
// filters / sorts / paginates / selects in memory. Server-side support
// would land as a follow-up `controlled` mode.
//
// State that lives in the URL: sorting, per-column filters, page index.
// Selection is transient (not persisted). When `persistInUrl=false` the
// state is local to the component.

const SKELETON_ROW_COUNT = 6;

// One canonical set of row chrome — matches the Table primitive's
// striping / borders / hover so the grid reads as part of the same
// family without re-exporting the same paint twice.
const HEAD_CLASSNAME = 'tw-h-12 tw-px-4 tw-align-middle tw-bg-muted tw-text-muted-foreground';
const HEAD_SELECTION_CLASSNAME = 'tw-h-12 tw-px-3 tw-align-middle tw-bg-muted tw-text-muted-foreground tw-w-px';
// `tw-truncate` is the default for body cells — single-line content
// with ellipsis when it exceeds the column's allocated width. Keeps
// row heights uniform (the virtualizer's ESTIMATED_ROW_HEIGHT assumes
// it) and removes the "long names wrap to 3 lines" failure mode in
// flex columns. Cells with custom `render` decide their own internal
// layout; the truncation still bounds them at the cell edge.
const CELL_CLASSNAME = 'tw-p-4 tw-align-middle tw-text-sm tw-leading-normal tw-truncate';

// Estimated height of a populated body row. Empirically measured at
// 59px: `tw-p-4` (32px vertical padding) + text content (~26px under
// `tw-text-sm tw-leading-normal` after browser line-box rounding) +
// 1px collapsed bottom border. Used by the row virtualizer to compute
// padding-row spacers — off-by-a-pixel here only shifts the scroll
// baseline, not the rendered layout (cells still size to their actual
// content).
const ESTIMATED_ROW_HEIGHT = 59;
// Overscan: rows rendered beyond the visible window on each side.
// Larger overscan = smoother scroll (more pre-rendered DOM ready to
// reveal as the user drags) at the cost of slightly more DOM. 20 on
// each side ≈ a full extra viewport above and below for the typical
// page size we see in this app, which keeps a fast drag from
// flashing blank rows before the virtualizer catches up.
const ROW_OVERSCAN = 20;
// Selection cell uses tighter horizontal padding (`tw-px-3` = 12) than
// body cells (`tw-p-4` = 16) so the column stays narrow. Vertical
// padding (`tw-py-4` = 16) matches body cells. Centering is handled by
// the wrapper around the Checkbox inside `buildSelectionColumn`, not
// by these padding values — the wrapper uses flex/justify-center
// because the Checkbox primitive renders a block-level flex div that
// ignores text-align.
const CELL_SELECTION_CLASSNAME = 'tw-px-3 tw-py-4 tw-align-middle tw-text-sm tw-leading-normal';
// `last:tw-border-b-0` drops the bottom border on the final tbody row so
// it doesn't paint on top of the scroll container's own bottom border —
// otherwise the two 1px lines stack at the same pixel and read as a
// 2px-thick border under the last row.
const ROW_BASE_CLASSNAME = 'tw-border-b tw-border-solid tw-border-border tw-transition-colors last:tw-border-b-0';
const ROW_ODD_CLASSNAME = 'tw-bg-background';
const ROW_EVEN_CLASSNAME = 'tw-bg-muted';
const ROW_INTERACTIVE_CLASSNAME = 'tw-cursor-pointer hover:tw-bg-secondary-surface';
const ROW_STATIC_CLASSNAME = 'tw-cursor-default';
// `tw-text-sm` on the table sets the body font size that cells inherit.
// `tw-table-fixed` is what keeps column widths stable as the virtualizer
// swaps rows in/out of the DOM (without it, table-auto would re-measure
// widths from currently-mounted row content and visually fluctuate).
const TABLE_CLASSNAME = 'tw-w-full tw-table-fixed tw-caption-bottom tw-border-collapse tw-text-sm';
// `tw-border-b` on the head separates it from the body. `tw-sticky tw-top-0`
// pins it to the top of the scroll container so the column titles stay
// visible while body rows scroll under it.
const THEAD_CLASSNAME = 'tw-sticky tw-top-0 tw-z-10 tw-border-b tw-border-solid tw-border-border tw-bg-muted';
// Centred body placeholder for empty / no-matches branches. The Text
// inside the cell needs the cell to set `text-align: center` because
// Text renders as a `<span>` and inherits text alignment from the
// nearest block-level ancestor.
const PLACEHOLDER_CELL_CLASSNAME = 'tw-p-6 tw-text-center';

const ALIGN_CLASSNAME = {
    start: 'tw-text-left',
    center: 'tw-text-center',
    end: 'tw-text-right'
};

const composeRowClassName = (isEven, isInteractive) => [
    ROW_BASE_CLASSNAME,
    isEven ? ROW_EVEN_CLASSNAME : ROW_ODD_CLASSNAME,
    isInteractive ? ROW_INTERACTIVE_CLASSNAME : ROW_STATIC_CLASSNAME
].join(' ');

// Built-in selection column. Carries no label, sort or filter — just the
// Checkboxes. Width is locked to 48px (checkbox + 12px gutter on each
// side); the column is never flex so the selection box doesn't drift.
// The header cell holds the "select all" Checkbox with an indeterminate
// state when only some rows are selected.
const buildSelectionColumn = () => ({
    id: 'selection',
    enableSorting: false,
    enableColumnFilter: false,
    meta: { align: 'center', isSelection: true, minWidth: 48, flex: false, hasCustomRender: true },
    header: ({ table }) => {
        const allSelected = table.getIsAllRowsSelected();
        const someSelected = table.getIsSomeRowsSelected();
        const checked = allSelected ? true : someSelected ? 'indeterminate' : false;

        // Wrap in a flex/justify-center div: the Checkbox primitive
        // renders its outer container as a block-level `tw-flex`, which
        // ignores the cell's `text-align: center`. A wrapper that's
        // also flex with `justify-center` overrides it and pins the
        // checkbox to the horizontal centre of the cell.
        return (
            <div className="tw-flex tw-justify-center">
                <Checkbox
                    aria-label={i18next.t('datagrid.select_all', 'Select all')}
                    checked={checked}
                    onCheckedChange={value => table.toggleAllRowsSelected(!!value)}
                />
            </div>
        );
    },
    cell: ({ row }) => (
        <div className="tw-flex tw-justify-center">
            <Checkbox
                aria-label={i18next.t('datagrid.select_row', 'Select row')}
                checked={row.getIsSelected()}
                disabled={!row.getCanSelect()}
                onCheckedChange={value => row.toggleSelected(!!value)}
            />
        </div>
    )
});

const adaptColumn = column => ({
    accessorKey: column.key,
    id: column.key,
    header: column.label,
    cell: column.render ? ({ row }) => column.render(row.original) : ({ getValue }) => getValue(),
    enableSorting: column.sortable === true,
    enableColumnFilter: column.filter !== undefined && column.filter !== null,
    enableHiding: true,
    filterFn: column.filter ? buildFilterFn(column.filter) : undefined,
    sortingFn: column.sortingFn ?? 'alphanumeric',
    meta: {
        align: column.align ?? 'start',
        label: column.label,
        filter: column.filter ? resolveFilterMeta(column.filter) : null,
        // `minWidth` (pixels, number) is the column's floor — never
        // shrinks below it. `flex: true` lets the column absorb slack
        // when the container is wider than the sum of minWidths.
        //
        // Multiple flex columns share the slack equally (a CSS
        // table-fixed constraint when columns omit their `<col width>`).
        // If consumers need per-column min enforcement across multiple
        // flex cols, either keep their minWidths uniform or fall back
        // to non-flex (paying with a wider fixed column).
        minWidth: typeof column.minWidth === 'number' ? column.minWidth : null,
        flex: column.flex === true,
        // Tracks whether the column has a consumer-provided `render`.
        // The DataGrid auto-injects `title={value}` on `<td>` for cells
        // that don't have a custom render, so the user can hover to
        // read the full content after truncation. Custom renders own
        // their tooltip — they may show a Badge, an Icon, or richer
        // content where the raw value isn't a useful tooltip string.
        hasCustomRender: typeof column.render === 'function'
    }
});

// Build the columnVisibility map TanStack expects ({ [key]: boolean }) from
// the column list, the URL's hidden set, and the "user has touched the
// popover" flag (`overridden`).
//
// Rule:
//   - overridden=true  → URL is the source of truth. A column is visible
//                        iff it is NOT in `hiddenSet`. `visible: false`
//                        defaults are ignored.
//   - overridden=false → URL hasn't been touched; fall back to the
//                        per-column `visible` default (true unless the
//                        column explicitly opts out with `visible: false`).
//
// The two cases map cleanly to the URL: `overridden` mirrors "the
// `?hide=` param is present" (even if its value is empty). An empty
// `?hide=` is the user's way of saying "I've decided I want everything
// visible, please don't re-apply the column defaults next time."
//
// The selection column is intentionally absent from the map. TanStack
// keeps any column missing from `columnVisibility` at its default
// (true), and the popover never lists it.
const buildVisibilityMap = (columns, hiddenSet, overridden) => {
    const visibility = {};
    columns.forEach(column => {
        if (overridden) {
            visibility[column.key] = !hiddenSet.has(column.key);
        } else {
            visibility[column.key] = column.visible !== false;
        }
    });
    return visibility;
};

const DataGrid = ({
    actions = [],
    columns,
    defaultSort = [],
    emptyState = null,
    loading = false,
    noMatchesState = null,
    onRowClick,
    onSelectionChange,
    pageSize = undefined,
    persistInUrl = true,
    rowKey = 'id',
    rows,
    selectable = false
}) => {
    React.useEffect(() => {
        const seen = new Set();
        columns.forEach(column => {
            if (seen.has(column.key)) {

                console.warn(`DataGrid: duplicate column key "${column.key}". Column keys must be unique.`);
            }
            seen.add(column.key);
            if (typeof column.minWidth !== 'number') {

                console.warn(`DataGrid: column "${column.key}" is missing \`minWidth\` (number, pixels). Required so the column never shrinks below its floor and the table can compute its scroll threshold.`);
            }
        });
    }, [columns]);

    // ─── Pagination size resolution ────────────────────────
    // `pageSize` accepts three shapes:
    //   - undefined (default, recommended) : show every row, no paginator.
    //                The body is virtualized — only the visible window of
    //                rows is ever mounted, so 10 rows and 10 000 rows pay
    //                roughly the same DOM cost. Pair with a parent that
    //                gives the grid a bounded height (`tw-h-full
    //                tw-min-h-0` on the page wrapper is enough) so the
    //                internal overflow-auto + virtualizer have something
    //                to measure against.
    //   - number    : fixed N rows per page. Caller wants explicit
    //                paginated browsing.
    //   - 'auto'    : measure the scroll container once on mount and pick
    //                 the page size that fits exactly. Now mostly obsolete
    //                 since virtualization makes "show all" cheap — kept
    //                 for callsites that genuinely want the paginator UI
    //                 sized to the viewport.
    const containerRef = React.useRef(null);
    const scrollContainerRef = React.useRef(null);
    const autoPageSize = useAutoPageSize(scrollContainerRef, pageSize === 'auto');
    // During the first render the hook hasn't measured yet — fall back to a
    // small page (5 rows) so the body has measurable rows by the time the
    // requestAnimationFrame callback fires. `isAutoMeasured` lets us know
    // when the real value is in: after that, the scroll container drops its
    // `flex-1` so it shrinks to the table's actual height and a separate
    // spacer absorbs the leftover space between the table and the footer.
    const isAutoMeasured = pageSize === 'auto' && autoPageSize !== null;
    const resolvedPageSize = pageSize === 'auto'
        ? (autoPageSize ?? 5)
        : (typeof pageSize === 'number' ? pageSize : null);
    const paginated = typeof resolvedPageSize === 'number' && resolvedPageSize > 0;

    // ─── URL ↔ state binding ───────────────────────────────
    const url = useDataGridUrlState({
        persist: persistInUrl,
        defaultSort,
        defaultPageIndex: 0
    });

    // ─── Selection state (always local; not URL-persisted) ─
    const [rowSelection, setRowSelection] = React.useState({});

    // ─── TanStack column shape ─────────────────────────────
    const tableColumns = React.useMemo(() => {
        const adapted = columns.map(adaptColumn);
        return selectable ? [buildSelectionColumn(), ...adapted] : adapted;
    }, [columns, selectable]);

    // Column visibility map for TanStack, derived from `columns` (for
    // `visible: false` defaults) and the URL state (hidden set +
    // override flag). The map is recomputed whenever any input
    // changes; we feed it into the table via `state.columnVisibility`.
    const columnVisibility = React.useMemo(
        () => buildVisibilityMap(columns, url.hiddenColumns, url.hiddenOverridden),
        [columns, url.hiddenColumns, url.hiddenOverridden]
    );

    const getRowId = React.useMemo(() => {
        if (typeof rowKey === 'function') {
            return row => String(rowKey(row));
        }
        return row => String(row[rowKey]);
    }, [rowKey]);

    const table = useReactTable({
        data: rows,
        columns: tableColumns,
        state: {
            sorting: url.sorting,
            columnFilters: url.columnFilters,
            columnVisibility,
            pagination: {
                pageIndex: url.pageIndex,
                pageSize: paginated ? resolvedPageSize : rows.length || 1
            },
            rowSelection
        },
        enableMultiSort: true,
        enableRowSelection: selectable,
        enableHiding: true,
        getRowId,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: paginated ? getPaginationRowModel() : undefined,
        onSortingChange: updater => {
            const next = typeof updater === 'function' ? updater(url.sorting) : updater;
            url.setSorting(next);
        },
        onColumnFiltersChange: updater => {
            const next = typeof updater === 'function' ? updater(url.columnFilters) : updater;
            url.setColumnFilters(next);
        },
        onColumnVisibilityChange: updater => {
            const next = typeof updater === 'function' ? updater(columnVisibility) : updater;
            // TanStack hands us a per-column boolean map; we translate
            // back to a "hidden set" for the URL. Only user-defined
            // columns participate — the selection column never appears
            // here because it isn't in the visibility map we feed in.
            const nextHidden = new Set();
            columns.forEach(column => {
                if (next[column.key] === false) {
                    nextHidden.add(column.key);
                }
            });
            url.setHiddenColumns(nextHidden);
        },
        onPaginationChange: updater => {
            const previous = { pageIndex: url.pageIndex, pageSize: resolvedPageSize ?? 0 };
            const next = typeof updater === 'function' ? updater(previous) : updater;
            url.setPageIndex(next.pageIndex);
        },
        onRowSelectionChange: setRowSelection
    });

    // ─── Selection callback (fire on changes) ─────────────
    const selectedRowKeysSig = Object.keys(rowSelection).filter(key => rowSelection[key]).join(',');

    React.useEffect(() => {
        if (!onSelectionChange) {
            return;
        }
        const selectedRows = table.getSelectedRowModel().rows.map(row => row.original);
        const selectedKeys = selectedRows.map(getRowId);
        onSelectionChange(selectedKeys, selectedRows);
        // We deliberately depend on the *signature* of the selection (key set)
        // rather than the table instance — selection callbacks should fire only
        // when the user changes the selection, not on every render of the table.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRowKeysSig]);

    // ─── Filtered / paginated derived counts ──────────────
    const totalRowCount = rows.length;
    const filteredRowCount = table.getFilteredRowModel().rows.length;
    const visibleRows = table.getRowModel().rows;
    // Visible leaf columns (TanStack column instances, not column defs)
    // for everything that has to count or position by *visible* column
    // index: colgroup widths, skeleton cells, colSpan on empty rows,
    // colSpan on virtualizer padding spacers. With `table-fixed` in
    // effect, the colgroup is position-sensitive — emitting a `<col>`
    // for a hidden column would shift every following width onto the
    // wrong cell.
    const visibleLeafColumns = table.getVisibleLeafColumns();
    const visibleColumnCount = visibleLeafColumns.length;
    // Sum of every visible column's `minWidth`. Applied as `min-width`
    // on the `<table>` so the scroll container can detect overflow when
    // the viewport is narrower than the layout floor. When the viewport
    // is wider, `tw-w-full` keeps the table at 100% and the slack flows
    // to flex columns.
    const tableMinWidth = visibleLeafColumns.reduce((sum, col) => {
        const colMin = col.columnDef.meta?.minWidth;
        return sum + (typeof colMin === 'number' ? colMin : 0);
    }, 0);
    // Implicit-flex fallback. When no column declares `flex: true` AND
    // there are no flex columns visible, the last visible non-selection
    // column absorbs slack instead — otherwise CSS `table-fixed` would
    // distribute slack proportionally across every column, including
    // narrow chrome like the selection checkbox (it would grow from
    // 48px to whatever fraction). The fallback keeps the selection
    // tight and every declared minWidth respected; the trade-off is
    // that the chosen "absorber" col can render visibly wider than its
    // siblings when the slack is large.
    const hasExplicitFlex = visibleLeafColumns.some(col => col.columnDef.meta?.flex === true);
    const implicitFlexColumnId = (() => {
        if (hasExplicitFlex) {
            return null;
        }
        for (let i = visibleLeafColumns.length - 1; i >= 0; i -= 1) {
            const col = visibleLeafColumns[i];
            if (!col.columnDef.meta?.isSelection) {
                return col.id;
            }
        }
        return null;
    })();

    // Row virtualizer keeps the populated body cheap regardless of how
    // many rows the caller hands us — particularly important when
    // `pageSize` is undefined (no paginator, full dataset rendered) and
    // the dataset is large. With pageSize=auto or pageSize=<number>,
    // `visibleRows.length` is already bounded by the page, so the
    // virtualizer is a near-no-op there. Always-on for simplicity; the
    // skeleton / empty / no-matches branches don't consult the
    // virtualizer (their bodies are content-sized).
    const rowVirtualizer = useVirtualizer({
        count: visibleRows.length,
        estimateSize: () => ESTIMATED_ROW_HEIGHT,
        getScrollElement: () => scrollContainerRef.current,
        overscan: ROW_OVERSCAN
    });

    const pageCount = paginated ? table.getPageCount() : 1;
    const currentPage = url.pageIndex + 1;
    const isRangeShown = paginated && filteredRowCount > 0;
    const rangeStart = isRangeShown ? url.pageIndex * resolvedPageSize + 1 : null;
    const rangeEnd = isRangeShown ? Math.min((url.pageIndex + 1) * resolvedPageSize, filteredRowCount) : null;

    // ─── Selection numbers for action gating ──────────────
    const selectedRowModel = table.getSelectedRowModel();
    const selectedRows = selectedRowModel.rows.map(row => row.original);
    const selectedKeys = selectedRowModel.rows.map(row => row.id);

    // ─── Body content (loading / empty / no-matches / rows) ─
    let body;
    if (loading) {
        body = (
            <tbody>
                {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
                    <tr key={`skeleton-${index}`} className={composeRowClassName(index % 2 === 1, false)}>
                        {visibleLeafColumns.map(column => (
                            <td key={column.id} className={CELL_CLASSNAME}>
                                <div className="tw-h-4 tw-w-full">
                                    <Skeleton tone="strong"/>
                                </div>
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        );
    } else if (totalRowCount === 0) {
        body = (
            <tbody>
                <tr className={ROW_ODD_CLASSNAME}>
                    <td className={PLACEHOLDER_CELL_CLASSNAME} colSpan={visibleColumnCount}>
                        {emptyState ?? <Text tone="muted" variant="body">{i18next.t('datagrid.no_data', 'No data')}</Text>}
                    </td>
                </tr>
            </tbody>
        );
    } else if (filteredRowCount === 0) {
        body = (
            <tbody>
                <tr className={ROW_ODD_CLASSNAME}>
                    <td className={PLACEHOLDER_CELL_CLASSNAME} colSpan={visibleColumnCount}>
                        {noMatchesState ?? <Text tone="muted" variant="body">{i18next.t('datagrid.no_matches', 'No matches')}</Text>}
                    </td>
                </tr>
            </tbody>
        );
    } else {
        const isInteractive = typeof onRowClick === 'function';

        // Padding rows preserve `<table>` column alignment. Using
        // `position: absolute` on a `<tr>` breaks the table layout
        // engine; instead, a leading `<tr>` reserves the height of
        // all rows above the visible window, and a trailing `<tr>`
        // reserves the height of all rows below it. Inside the
        // middle we render only the virtual window's rows — usually
        // ~15–20 even when `visibleRows.length` is in the thousands.
        const virtualItems = rowVirtualizer.getVirtualItems();
        const totalSize = rowVirtualizer.getTotalSize();
        const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
        const paddingBottom = virtualItems.length > 0
            ? totalSize - virtualItems[virtualItems.length - 1].end
            : 0;

        body = (
            <tbody>
                {paddingTop > 0 ? (
                    <tr aria-hidden="true">
                        <td colSpan={visibleColumnCount} style={{ height: `${paddingTop}px` }}/>
                    </tr>
                ) : null}
                {virtualItems.map(virtualRow => {
                    const row = visibleRows[virtualRow.index];
                    return (
                        <tr
                            key={row.id}
                            className={composeRowClassName(virtualRow.index % 2 === 1, isInteractive)}
                            onClick={event => {
                                if (!isInteractive) {
                                    return;
                                }
                                // Skip clicks inside the selection cell
                                // so checkbox toggling doesn't double-
                                // fire the row action.
                                const target = event.target;
                                if (target.closest('[data-datagrid-skip-row-click]')) {
                                    return;
                                }
                                onRowClick(row.original);
                            }}
                        >
                            {row.getVisibleCells().map(cell => {
                                const meta = cell.column.columnDef.meta;
                                const align = meta?.align ?? 'start';
                                const isSelection = meta?.isSelection;
                                const hasCustomRender = meta?.hasCustomRender;
                                const cellClassName = isSelection ? CELL_SELECTION_CLASSNAME : CELL_CLASSNAME;
                                // Auto-tooltip: when the cell uses the
                                // default `getValue()` renderer, expose
                                // the full text on hover so users can
                                // recover the part that the truncation
                                // hid. Custom renders own their own
                                // tooltip (their value may be a Badge,
                                // an Icon, or a transformed string).
                                const rawValue = !hasCustomRender ? cell.getValue() : null;
                                const title = !hasCustomRender && rawValue !== null && rawValue !== undefined && rawValue !== ''
                                    ? String(rawValue)
                                    : undefined;
                                return (
                                    <td
                                        key={cell.id}
                                        className={`${cellClassName} ${ALIGN_CLASSNAME[align]}`}
                                        data-datagrid-skip-row-click={isSelection ? '' : undefined}
                                        title={title}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                );
                            })}
                        </tr>
                    );
                })}
                {paddingBottom > 0 ? (
                    <tr aria-hidden="true">
                        <td colSpan={visibleColumnCount} style={{ height: `${paddingBottom}px` }}/>
                    </tr>
                ) : null}
            </tbody>
        );
    }

    // Layout height depends on the pageSize mode:
    //   - 'auto'    → fill parent height (`tw-h-full`), so useAutoPageSize
    //                 can measure available space and the scroll area can
    //                 flex inside the remaining room.
    //   - undefined → fill parent height too: with no paginator the whole
    //                 dataset goes through the virtualizer, which needs a
    //                 bounded scroll container to know which rows are in
    //                 view. Without `flex-1` the body would grow to fit
    //                 every row, the parent would scroll, and the
    //                 virtualizer (listening on the body container) would
    //                 never see scroll events — rendering only the
    //                 initial window.
    //   - number    → take natural height (rows × row-height); the card
    //                 stops growing once the body is drawn. Without this
    //                 guard a parent with a fixed height (e.g. 520px)
    //                 bleeds empty space under the last row.
    const isAutoFit = pageSize === 'auto';
    const fillsParent = isAutoFit || pageSize === undefined;
    const containerClassName = fillsParent
        ? 'tw-flex tw-h-full tw-min-h-0 tw-flex-col tw-gap-3'
        : 'tw-flex tw-flex-col tw-gap-3';
    // Four states for the scroll container's height:
    //   - auto / measuring (`isAutoFit && !isAutoMeasured`): `flex-1`
    //     inside the table-block wrapper so the hook can read the
    //     available height from clientHeight.
    //   - auto / measured (`isAutoMeasured`): natural height. The
    //     bordered box ends exactly at the last row; the caption sits
    //     right under it and the wrapper absorbs the leftover space.
    //   - undefined (virtualized show-all): `flex-1` permanently so the
    //     virtualizer always has a bounded scroll element.
    //   - number: natural height (no `flex-1`).
    const scrollContainerFlex = (isAutoFit && !isAutoMeasured) || pageSize === undefined;
    const scrollContainerClassName = scrollContainerFlex
        ? 'tw-relative tw-flex-1 tw-overflow-auto tw-rounded-lg tw-border tw-border-solid tw-border-border'
        : 'tw-relative tw-overflow-auto tw-rounded-lg tw-border tw-border-solid tw-border-border';

    // The table-block wrapper groups the scroll container with the caption
    // immediately below it, with no `gap` between them so the caption
    // visually attaches to the table's bottom edge. In auto and
    // undefined modes the wrapper takes `flex-1` of the outer container
    // so its bottom edge sits at the page bottom — the paginator
    // (sibling of the wrapper) is then pinned by `tw-gap-3` of the
    // outer container right under it.
    const tableBlockClassName = fillsParent
        ? 'tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-1'
        : 'tw-flex tw-flex-col tw-gap-1';

    // ─── Column visibility chrome (toolbar popover) ────────
    // The toolbar lists every user-defined column with a checkbox and a
    // hard floor of 2 visible "real" columns. The selection column is
    // chrome — it never appears in the list and never counts toward the
    // floor.
    //
    // `canHideMore` is precomputed here so the toolbar doesn't have to
    // do arithmetic over the column list every render — and so the
    // floor logic lives next to the visibility wiring it depends on.
    const visibleRealColumnCount = columns.reduce(
        (count, column) => count + (columnVisibility[column.key] === false ? 0 : 1),
        0
    );
    const canHideMore = visibleRealColumnCount > 2;
    const toolbarColumns = columns.map(column => ({
        key: column.key,
        label: column.label,
        visible: columnVisibility[column.key] !== false
    }));
    const setColumnVisible = (columnKey, visible) => {
        // Recompute the URL-shape hidden set from the current map plus
        // the requested toggle. Funneling through the table's setter
        // keeps URL persistence and TanStack state in lockstep.
        const next = { ...columnVisibility, [columnKey]: visible };
        table.setColumnVisibility(next);
    };

    // Suppress the Columns popover when no column opts itself hidden
    // by default — without a default-hidden subset there's nothing the
    // user needs to discover, and the affordance only adds chrome. As
    // soon as ANY column declares `visible: false` the control comes
    // back, since now there's a real "show this hidden one" use case.
    // The Toolbar hides its control whenever `onColumnVisibilityChange`
    // is missing, so we just stop passing it down in that case.
    const hasHiddenByDefault = columns.some(column => column.visible === false);

    return (
        <div ref={containerRef} className={containerClassName}>
            <DataGridToolbar
                actions={actions}
                canHideMore={canHideMore}
                columns={toolbarColumns}
                onColumnVisibilityChange={hasHiddenByDefault ? setColumnVisible : undefined}
                selectedKeys={selectedKeys}
                selectedRows={selectedRows}
            />

            {/* Table block: scroll container + caption stack with a tight
                gap so the caption reads as part of the table footer chrome.
                In auto / undefined modes the block is `flex-1` of the
                outer container — its bottom edge sits at the page bottom,
                with the paginator pinned below it via the outer `tw-gap-3`.
                `<colgroup>` + `tw-table-fixed` keep column widths stable as
                the virtualizer swaps rows in and out of the DOM (without
                them, table-auto would re-measure widths on every scroll
                from the currently-mounted row content). Columns without
                an explicit `width` in their definition share the
                remaining space equally. */}
            <div className={tableBlockClassName}>
                <div ref={scrollContainerRef} className={scrollContainerClassName}>
                    {/* `tw-border-collapse` is explicitly set: without it,
                        adjacent cell/row borders sit side-by-side instead
                        of merging — the thead's `border-b` would then
                        stack with whatever the first body row paints at
                        its top edge and read as a 2px-thick line. */}
                    <table
                        className={TABLE_CLASSNAME}
                        style={{ minWidth: `${tableMinWidth}px` }}
                    >
                        <colgroup>
                            {visibleLeafColumns.map(column => {
                                const meta = column.columnDef.meta;
                                const isExplicitFlex = meta?.flex === true;
                                const isImplicitFlex = column.id === implicitFlexColumnId;
                                const isFlex = isExplicitFlex || isImplicitFlex;
                                const minWidth = meta?.minWidth;
                                // Non-flex cols lock to their minWidth via
                                // `<col width>`. Flex cols (explicit OR
                                // the implicit-fallback last col) omit
                                // `width` entirely so `table-fixed` shares
                                // the remainder among them. The table's
                                // own `min-width` (set above) guarantees
                                // flex cols can't collapse below their
                                // declared floor — when the container
                                // would push the remainder below that,
                                // the table overflows into the parent's
                                // scroll.
                                const inlineWidth = !isFlex && typeof minWidth === 'number'
                                    ? { width: `${minWidth}px` }
                                    : undefined;
                                return (
                                    <col key={column.id} style={inlineWidth}/>
                                );
                            })}
                        </colgroup>
                        <thead className={THEAD_CLASSNAME}>
                            {table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map(header => {
                                        const isSelection = header.column.columnDef.meta?.isSelection;
                                        const headClassName = isSelection ? HEAD_SELECTION_CLASSNAME : HEAD_CLASSNAME;
                                        return (
                                            <th
                                                key={header.id}
                                                className={`${headClassName} ${ALIGN_CLASSNAME[header.column.columnDef.meta?.align ?? 'start']}`}
                                            >
                                                {isSelection
                                                    ? flexRender(header.column.columnDef.header, header.getContext())
                                                    : <DataGridHeaderCell header={header}/>}
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))}
                        </thead>
                        {body}
                    </table>
                </div>

                <DataGridFooter
                    filteredCount={filteredRowCount}
                    paginated={paginated}
                    rangeEnd={rangeEnd}
                    rangeStart={rangeStart}
                    selectedCount={selectedKeys.length}
                    totalCount={rows.length}
                />
            </div>

            {paginated && pageCount > 1 ? (
                <Pagination
                    currentPage={currentPage}
                    onChange={pageIndex => url.setPageIndex(pageIndex - 1)}
                    totalPages={pageCount}
                />
            ) : null}
        </div>
    );
};

DataGrid.displayName = 'DataGrid';

export { DataGrid };
